import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';

import { WrapFormGroup } from '../../common/form/abstract-form.adaptor';
import { AbstractRequestFormAdaptor } from '../../common/form/abstract-request-form.adaptor';
import { normalizePhoneAU } from '../../common/validators/phone-au.validator';
import { ContactSuccessModalComponent } from './contact-us-modal.component';
import { SuccessModalComponent } from '../components/modals/modal-success';

export interface ContactUsFormData {
  name: string;
  group: {
    email: string;
    phone_number: string;
  };
  contact_preference: string;
  comment: string;
}

export const CONTACT_US_API = '/contacts';

@Injectable()
export class ContactUsAdaptor extends AbstractRequestFormAdaptor<ContactUsFormData, object> {
  name = 'contact-us';

  private http = inject(HttpClient);
  private modalCtrl = inject(ModalController); // Inject ModalController

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override onRequest(formData: ContactUsFormData, _formGroup: WrapFormGroup<ContactUsFormData>) {
    const newFormData = {
      name: formData.name,
      email: formData.group?.email || '',
      phone_number: formData.group?.phone_number?.startsWith('+')
        ? formData.group.phone_number.replace(/[^\d+]/g, '')
        : normalizePhoneAU(formData.group?.phone_number) || '',
      contact_preference: formData.contact_preference,
      comment: formData.comment,
      preferred_time: '',
    };

    return this.http.post(CONTACT_US_API, {
      contact: { ...newFormData },
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override async onSuccess() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const modal = await this.modalCtrl.create({
      component: ContactSuccessModalComponent, // Open the modal component
      cssClass: 'custom-contact-modal modal-box custom-modal',
    });
    await modal.present();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  override async onError(
    _formData: ContactUsFormData,
    errorRes: HttpErrorResponse,
    formGroup: WrapFormGroup<ContactUsFormData>,
  ) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    // formGroup.reset();
    this.markAllControlsAsTouched(formGroup);

    const errorMessage = this.getErrorMessage(errorRes);

    const errorModal = await this.modalCtrl.create({
      component: SuccessModalComponent,
      cssClass: 'modal-box custom-error-modal',
      componentProps: {
        title: 'Error',
        message: errorMessage,
        buttons: [
          {
            text: 'Close message',
            fill: 'clear',
            cssClass: 'tertiaryBtn',
            handler: (): void => {
              errorModal.dismiss();
            },
          },
        ],
      },
    });

    await errorModal.present();
  }

  private markAllControlsAsTouched(formGroup: WrapFormGroup<ContactUsFormData>): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private getErrorMessage(errorRes: HttpErrorResponse): string {
    const serverError = errorRes.error?.error;
    const defaultMessage = 'We are sorry. There was an error.<br>Your request was not sent.<br>Please try again later.';

    return serverError ? serverError + defaultMessage : defaultMessage;
  }
}
